/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { AgreementDetailsFragment } from "@cosmos/hygraph-schema";
import { useRouter, useSearchParams } from "next/navigation";
import { ComponentType, useCallback, useEffect, useState } from "react";
import { isSameOrigin } from "@cosmos/common-utils";

import { adjustAcceptedRedirect } from "./eula-agreement.utils";
import { EulaAgreementDetails, EulaAgreementDetailsMainProps } from "./EulaAgreementDetails.organism";

import type { useEulaAcceptance } from "@cosmos/next-hooks";
import { SizedLoadingProps } from "@components/types";

export interface EulaAgreementProps extends EulaAgreementDetailsMainProps {
	LoadingComponent?: ComponentType<SizedLoadingProps>;
	locale: string;
	version?: number | null;
	agreementDetails?: Partial<AgreementDetailsFragment> | null;
	alreadyAccepted?: boolean;
	useEulaAcceptance: typeof useEulaAcceptance;
}

export const EulaAgreementSection = ({
	locale,
	version,
	LoadingComponent,
	alreadyAccepted,
	useEulaAcceptance,
	agreementDetails,
	...props
}: EulaAgreementProps): React.JSX.Element => {
	const [executeSubmit, setExecuteSubmit] = useState(false);
	const {
		agreementKeyEula = "",
		acceptedRedirect,
		acceptedTimeout,
		acceptedContent,
		acceptOptionLabel,
		acceptButtonLabel,
		mustAcceptWarning,
	} = agreementDetails || {};

	const { accept, isLoading, error } = useEulaAcceptance(agreementKeyEula, "en", executeSubmit, version || 0);

	const accepted = alreadyAccepted || accept?.accepted;
	const adjustedCompletedPath = adjustAcceptedRedirect(locale, acceptedRedirect || "");
	let finalCompletedPath = adjustedCompletedPath;

	const searchParams = useSearchParams();
	const router = useRouter();
	const redirectState = searchParams.get("state");
	if (typeof redirectState === "string" && isSameOrigin(redirectState)) {
		const cleanedState = redirectState.replace(/^[/]{2,}/g, "/");
		if (cleanedState) {
			finalCompletedPath = cleanedState;
		}
	}

	// Strip malicious state param from the URL when it fails origin validation
	useEffect(() => {
		if (typeof redirectState !== "string" || isSameOrigin(redirectState)) return;
		const cleaned = new URLSearchParams(searchParams.toString());
		cleaned.delete("state");
		const qs = cleaned.toString();
		router.replace(`${window.location.pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
	}, [redirectState, searchParams, router]);

	const onSumbmit = useCallback(() => {
		setExecuteSubmit(true);
	}, [setExecuteSubmit]);

	useEffect(() => {
		if (!finalCompletedPath || !accepted) return;
		if (!acceptedTimeout) return router.push(finalCompletedPath);
		const timer = setTimeout(() => {
			router.push(finalCompletedPath);
		}, acceptedTimeout);
		return () => clearTimeout(timer);
	}, [accepted, router, finalCompletedPath, acceptedTimeout]);

	if (accept && isLoading && LoadingComponent) {
		return <LoadingComponent size={200} />;
	}

	return (
		<EulaAgreementDetails
			{...props}
			onSubmit={onSumbmit}
			error={error}
			accepted={accepted}
			acceptedContent={acceptedContent}
			acceptOptionLabel={acceptOptionLabel}
			acceptButtonLabel={acceptButtonLabel}
			mustAcceptWarning={mustAcceptWarning}
			hideOptIn={false}
		/>
	);
};
